import { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import * as ssoApi from '../../api/sso';
import type { AssignableRole, SsoConnection } from '../../api/sso';
import { extractErrorMessage } from '../../api/errors';
import { SearchableSelect } from '../../components/SearchableSelect';

/**
 * Enterprise SSO connection config for one tenant (ENTERPRISE-SSO Phase 2).
 * Rendered as a Card on three surfaces:
 *   - the tenant's own Settings → Security → Single sign-on (this is where
 *     an admin looks for it; a tenant_admin gets `readOnly`)
 *   - Configuration → Enterprise SSO, behind a tenant picker (superadmin)
 *   - the superadmin Tenant Detail page
 *
 * `readOnly` mirrors the server's split: GET is open to the tenant's admins
 * so they can see their own SSO posture, but PUT/DELETE are superadmin-only
 * because `allowed_domains` maps a domain to exactly one tenant globally —
 * claiming a domain is a platform decision. The flag only hides controls the
 * API would reject anyway; it is not the security boundary.
 *
 * The broker connection id comes from the WorkOS dashboard once the
 * customer's IT wires up their IdP; the domains, default role, group→role
 * overrides, and the enforce / MFA / active switches are set here.
 */

const ROLE_OPTIONS: { value: AssignableRole; label: string }[] = [
  { value: 'participant', label: 'Participant' },
  { value: 'coordinator', label: 'Coordinator' },
  { value: 'tenant_admin', label: 'Tenant admin' },
];

interface GroupRow {
  group: string;
  role: AssignableRole;
}

function domainsToText(domains: string[]): string {
  return domains.join('\n');
}

function textToDomains(text: string): string[] {
  // Split on commas, whitespace, and newlines; drop blanks. The server
  // re-validates + lowercases + de-dupes, so we stay lenient here.
  return text
    .split(/[\s,]+/)
    .map((d) => d.trim())
    .filter(Boolean);
}

function mapToRows(map: Record<string, AssignableRole>): GroupRow[] {
  return Object.entries(map).map(([group, role]) => ({ group, role }));
}

function rowsToMap(rows: GroupRow[]): Record<string, AssignableRole> {
  const out: Record<string, AssignableRole> = {};
  for (const r of rows) {
    const g = r.group.trim();
    if (g) out[g] = r.role;
  }
  return out;
}

export function SsoConnectionPanel({
  tenantId,
  readOnly = false,
}: {
  tenantId: string;
  readOnly?: boolean;
}) {
  // `loaded` distinguishes "still fetching" from "fetched, none configured".
  const [loaded, setLoaded] = useState(false);
  const [connection, setConnection] = useState<SsoConnection | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Form state.
  const [brokerConnectionId, setBrokerConnectionId] = useState('');
  const [domainsText, setDomainsText] = useState('');
  const [defaultRole, setDefaultRole] = useState<AssignableRole>('participant');
  const [groupRows, setGroupRows] = useState<GroupRow[]>([]);
  const [enforceSso, setEnforceSso] = useState(false);
  const [ssoSatisfiesMfa, setSsoSatisfiesMfa] = useState(true);
  const [isActive, setIsActive] = useState(true);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Back to the pristine empty form. Needed because this panel can be
  // pointed at a different tenant while mounted (the Configuration tab's
  // picker): a tenant with no connection answers 404, which hydrates
  // nothing, so without an explicit reset the previous tenant's domains
  // would linger in the fields — one Save away from being claimed by the
  // wrong tenant.
  const resetForm = () => {
    setConnection(null);
    setBrokerConnectionId('');
    setDomainsText('');
    setDefaultRole('participant');
    setGroupRows([]);
    setEnforceSso(false);
    setSsoSatisfiesMfa(true);
    setIsActive(true);
  };

  const hydrate = (c: SsoConnection) => {
    setConnection(c);
    setBrokerConnectionId(c.broker_connection_id);
    setDomainsText(domainsToText(c.allowed_domains));
    setDefaultRole(c.default_role);
    setGroupRows(mapToRows(c.group_role_map));
    setEnforceSso(c.enforce_sso);
    setSsoSatisfiesMfa(c.sso_satisfies_mfa);
    setIsActive(c.is_active);
  };

  useEffect(() => {
    let alive = true;
    ssoApi
      .getSsoConnection(tenantId)
      .then((c) => {
        if (alive) hydrate(c);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        const status = (err as { response?: { status?: number } })?.response
          ?.status;
        // 404 = no connection configured yet; that's the empty-form state,
        // not an error.
        if (status === 404) {
          resetForm();
        } else {
          setLoadError(extractErrorMessage(err, 'failed to load SSO settings'));
        }
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [tenantId]);

  const onSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSavedAt(null);
    try {
      const saved = await ssoApi.putSsoConnection(tenantId, {
        broker_connection_id: brokerConnectionId.trim(),
        allowed_domains: textToDomains(domainsText),
        default_role: defaultRole,
        group_role_map: rowsToMap(groupRows),
        enforce_sso: enforceSso,
        sso_satisfies_mfa: ssoSatisfiesMfa,
        is_active: isActive,
      });
      hydrate(saved);
      setSavedAt(Date.now());
    } catch (err: unknown) {
      setSaveError(extractErrorMessage(err, 'failed to save SSO settings'));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    setDeleting(true);
    setSaveError(null);
    try {
      await ssoApi.deleteSsoConnection(tenantId);
      resetForm();
      setSavedAt(null);
      setConfirmDelete(false);
    } catch (err: unknown) {
      setSaveError(extractErrorMessage(err, 'failed to remove SSO connection'));
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="overline" color="text.secondary">
            Enterprise SSO
          </Typography>
          {connection &&
            (connection.is_active ? (
              <Chip label="Configured" size="small" color="success" />
            ) : (
              <Chip label="Inactive" size="small" color="warning" />
            ))}
        </Stack>
        <Divider sx={{ my: 1 }} />

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {readOnly ? (
            <>
              Users on the allowed domains sign in through your organization&apos;s
              identity provider instead of a password. Your 10NEWTONS
              administrator manages this connection — an email domain can
              belong to only one tenant across the platform, so the mapping
              is set platform-side.
            </>
          ) : (
            <>
              Wire this tenant to its identity provider through the SSO
              broker. Users on the allowed domains sign in through their
              organization instead of a password. The broker connection id
              comes from the WorkOS dashboard once the customer&apos;s IT
              connects their IdP.
            </>
          )}
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {loadError}
          </Alert>
        )}
        {!loaded && !loadError && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        )}

        {/* Read-only viewer, nothing configured: an empty disabled form says
            nothing. Say the actual state instead. */}
        {loaded && !loadError && readOnly && !connection && (
          <Alert severity="info">
            Single sign-on is not set up for this tenant — everyone signs in
            with an email and password. Ask your 10NEWTONS administrator to
            connect your identity provider.
          </Alert>
        )}

        {loaded && !loadError && (!readOnly || connection) && (
          <Stack spacing={2.5}>
            <TextField
              label="Broker connection ID"
              placeholder="conn_01H…"
              value={brokerConnectionId}
              onChange={(e) => setBrokerConnectionId(e.target.value)}
              disabled={saving || readOnly}
              fullWidth
              helperText="From the WorkOS dashboard for this tenant's connection."
            />

            <TextField
              label="Allowed email domains"
              placeholder="acme.org&#10;clinic.acme.org"
              value={domainsText}
              onChange={(e) => setDomainsText(e.target.value)}
              disabled={saving || readOnly}
              fullWidth
              multiline
              minRows={2}
              helperText="One per line (or comma-separated). Each domain maps to exactly one tenant."
            />

            <SearchableSelect<AssignableRole>
              id="sso-default-role"
              label="Default role"
              size="medium"
              value={defaultRole}
              onChange={setDefaultRole}
              options={ROLE_OPTIONS}
              disabled={saving || readOnly}
              sx={{ maxWidth: 260 }}
              helperText="Role granted when no group mapping matches."
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Group → role overrides
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Optional. When the IdP asserts one of these groups, grant
                that role instead of the default (most-privileged wins).
              </Typography>
              <Stack spacing={1}>
                {groupRows.map((row, i) => (
                  <Grid container spacing={1} key={i} alignItems="center">
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        label="IdP group"
                        value={row.group}
                        onChange={(e) =>
                          setGroupRows((rows) =>
                            rows.map((r, j) =>
                              j === i ? { ...r, group: e.target.value } : r,
                            ),
                          )
                        }
                        disabled={saving || readOnly}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={{ xs: 9, sm: 5 }}>
                      <SearchableSelect<AssignableRole>
                        id={`sso-group-role-${i}`}
                        label="Role"
                        value={row.role}
                        onChange={(v) =>
                          setGroupRows((rows) =>
                            rows.map((r, j) =>
                              j === i ? { ...r, role: v } : r,
                            ),
                          )
                        }
                        options={ROLE_OPTIONS}
                        disabled={saving || readOnly}
                        size="small"
                        fullWidth
                      />
                    </Grid>
                    {!readOnly && (
                      <Grid size={{ xs: 3, sm: 1 }}>
                        <Tooltip title="Remove mapping">
                          <IconButton
                            aria-label="Remove mapping"
                            onClick={() =>
                              setGroupRows((rows) =>
                                rows.filter((_, j) => j !== i),
                              )
                            }
                            disabled={saving}
                            size="small"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                ))}
                {!readOnly && (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      setGroupRows((rows) => [
                        ...rows,
                        { group: '', role: 'participant' },
                      ])
                    }
                    disabled={saving}
                    size="small"
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Add mapping
                  </Button>
                )}
              </Stack>
            </Box>

            <Divider />

            <Stack spacing={0.5}>
              <FormControlLabel
                control={
                  <Switch
                    checked={enforceSso}
                    onChange={(e) => setEnforceSso(e.target.checked)}
                    disabled={saving || readOnly}
                  />
                }
                label="Enforce SSO (hide password login for these domains)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={ssoSatisfiesMfa}
                    onChange={(e) => setSsoSatisfiesMfa(e.target.checked)}
                    disabled={saving || readOnly}
                  />
                }
                label="SSO satisfies MFA (no separate 6-digit step)"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    disabled={saving || readOnly}
                  />
                }
                label="Active"
              />
            </Stack>

            {saveError && <Alert severity="error">{saveError}</Alert>}
            {savedAt !== null && !saveError && (
              <Alert severity="success">SSO settings saved.</Alert>
            )}

            {!readOnly && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  variant="contained"
                  onClick={() => void onSave()}
                  disabled={saving || !brokerConnectionId.trim()}
                >
                  {saving
                    ? 'Saving…'
                    : connection
                      ? 'Save changes'
                      : 'Enable SSO'}
                </Button>
                {connection && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setConfirmDelete(true)}
                    disabled={saving}
                  >
                    Remove connection
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Remove SSO connection?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Users on these domains will fall back to password login. Accounts
            and memberships already provisioned via SSO are kept — only the
            single-sign-on front door is removed. You can reconfigure it at
            any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={() => void onDelete()}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
